import React from "react";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Plus, Trash2 } from "lucide-react";

const inputClass = "bg-[#F6F1E8] border border-[#1A1714]/15 rounded-sm focus-visible:ring-1 focus-visible:ring-[#1A1714]";

const Block = ({ title, hint, children }) => (
  <div className="bg-[#EFE8DA] border border-[#1A1714]/15 rounded-sm p-6 md:p-7">
    <div className="font-serif-display text-xl">{title}</div>
    {hint && <p className="text-xs text-[#1A1714]/55 mt-1">{hint}</p>}
    <div className="mt-5 space-y-4">{children}</div>
  </div>
);

const Field = ({ label, hint, children }) => (
  <div>
    <label className="block text-[10px] tracking-[0.22em] uppercase text-[#1A1714]/60 mb-1.5">{label}</label>
    {children}
    {hint && <p className="text-[11px] text-[#1A1714]/50 mt-1">{hint}</p>}
  </div>
);

const Section = ({ title, hint, value, onChange, fields = ["eyebrow", "title", "titleEmphasis", "lede"], extras = null }) => {
  const set = (key, v) => onChange({ ...(value || {}), [key]: v });
  return (
    <Block title={title} hint={hint}>
      {fields.includes("eyebrow") && (
        <Field label="Eyebrow (small label above title)">
          <Input className={inputClass} value={value?.eyebrow || ""} onChange={(e) => set("eyebrow", e.target.value)} />
        </Field>
      )}
      {fields.includes("title") && (
        <Field label="Section title">
          <Input className={inputClass} value={value?.title || ""} onChange={(e) => set("title", e.target.value)} />
        </Field>
      )}
      {fields.includes("titleEmphasis") && (
        <Field label="Emphasised words" hint="A substring of the title that will appear in italic accent colour. Leave blank for no emphasis.">
          <Input className={inputClass} value={value?.titleEmphasis || ""} onChange={(e) => set("titleEmphasis", e.target.value)} />
        </Field>
      )}
      {fields.includes("lede") && (
        <Field label="Sub-text (lede paragraph)">
          <Textarea rows={3} className={inputClass} value={value?.lede || ""} onChange={(e) => set("lede", e.target.value)} />
        </Field>
      )}
      {extras}
    </Block>
  );
};

export default function AdminCopy({ copy, onChange }) {
  const setSection = (key, val) => onChange({ ...(copy || {}), [key]: val });
  const c = copy || {};

  // Marquee handler
  const marquee = c.marquee || [];
  const setMarquee = (m) => setSection("marquee", m);

  return (
    <div className="space-y-5 max-w-4xl">
      <p className="text-[#1A1714]/65 text-sm">
        Every visible heading, eyebrow and sub-text on your site lives here. Changes go live when you click <strong>Save changes</strong>.
      </p>

      {/* Hero */}
      <Section
        title="Hero (top of page)"
        value={c.hero}
        onChange={(v) => setSection("hero", v)}
        fields={[]}
        extras={
          <>
            <Field label="Eyebrow above the big name" hint="e.g. PORTFOLIO · 2025">
              <Input className={inputClass} value={c.hero?.eyebrow || ""} onChange={(e) => setSection("hero", { ...(c.hero || {}), eyebrow: e.target.value })} />
            </Field>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Field label="Primary button"><Input className={inputClass} value={c.hero?.primaryCta || ""} onChange={(e) => setSection("hero", { ...(c.hero || {}), primaryCta: e.target.value })} /></Field>
              <Field label="CV button (when uploaded)"><Input className={inputClass} value={c.hero?.cvCta || ""} onChange={(e) => setSection("hero", { ...(c.hero || {}), cvCta: e.target.value })} /></Field>
              <Field label="CV placeholder (when not uploaded)"><Input className={inputClass} value={c.hero?.cvComingSoon || ""} onChange={(e) => setSection("hero", { ...(c.hero || {}), cvComingSoon: e.target.value })} /></Field>
            </div>
            <p className="text-xs text-[#1A1714]/55">Tip: your name and tagline are edited under the <em>Profile</em> tab.</p>
          </>
        }
      />

      {/* Marquee */}
      <Block title="Marquee strip" hint="The scrolling italic words just below the hero. Add/remove freely.">
        <div className="flex flex-wrap gap-2">
          {marquee.map((w, i) => (
            <span key={i} className="inline-flex items-center gap-2 bg-[#1A1714] text-[#F6F1E8] px-3 py-1.5 rounded-full text-[12px] tracking-[0.16em] uppercase">
              <input
                value={w}
                onChange={(e) => setMarquee(marquee.map((x, k) => (k === i ? e.target.value : x)))}
                className="bg-transparent outline-none border-none w-[140px]"
              />
              <button type="button" onClick={() => setMarquee(marquee.filter((_, k) => k !== i))}><Trash2 size={12} /></button>
            </span>
          ))}
          <button type="button" onClick={() => setMarquee([...marquee, "New word"])} className="inline-flex items-center gap-2 border border-dashed border-[#1A1714]/30 px-3 py-1.5 rounded-full text-[12px] tracking-[0.18em] uppercase hover:bg-[#1A1714]/5">
            <Plus size={12} /> Add word
          </button>
        </div>
      </Block>

      {/* About */}
      <Section
        title="About section"
        value={c.about}
        onChange={(v) => setSection("about", v)}
        fields={["eyebrow", "title", "titleEmphasis"]}
        extras={
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Portrait caption (bottom-left)" hint="e.g. CAPE TOWN · ZA">
              <Input className={inputClass} value={c.about?.portraitCaption || ""} onChange={(e) => setSection("about", { ...(c.about || {}), portraitCaption: e.target.value })} />
            </Field>
            <Field label="Portrait figure (bottom-right)" hint='e.g. Fig. 01'>
              <Input className={inputClass} value={c.about?.portraitFigure || ""} onChange={(e) => setSection("about", { ...(c.about || {}), portraitFigure: e.target.value })} />
            </Field>
          </div>
        }
      />

      {/* Work */}
      <Section title="Selected Work section" value={c.work} onChange={(v) => setSection("work", v)} />

      {/* Showreel */}
      <Section title="Showreel section" value={c.showreel} onChange={(v) => setSection("showreel", v)} />

      {/* Skills */}
      <Section title="Skills / Capabilities section" value={c.skills} onChange={(v) => setSection("skills", v)} fields={["eyebrow", "title", "titleEmphasis"]} />

      {/* Experience */}
      <Section
        title="Experience section"
        value={c.experience}
        onChange={(v) => setSection("experience", v)}
        fields={["eyebrow", "title", "titleEmphasis"]}
        extras={
          <Field label="Side label (small text on the left)" hint="Shown beside the timeline. e.g. Roles & collaborations">
            <Input className={inputClass} value={c.experience?.asideLabel || ""} onChange={(e) => setSection("experience", { ...(c.experience || {}), asideLabel: e.target.value })} />
          </Field>
        }
      />

      {/* Education */}
      <Section title="Education section" value={c.education} onChange={(v) => setSection("education", v)} fields={["eyebrow", "title", "titleEmphasis"]} />

      {/* Contact */}
      <Section
        title="Contact section"
        value={c.contact}
        onChange={(v) => setSection("contact", v)}
        extras={
          <>
            <Field label="Form box title">
              <Input className={inputClass} value={c.contact?.formTitle || ""} onChange={(e) => setSection("contact", { ...(c.contact || {}), formTitle: e.target.value })} />
            </Field>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Success toast — title">
                <Input className={inputClass} value={c.contact?.successTitle || ""} onChange={(e) => setSection("contact", { ...(c.contact || {}), successTitle: e.target.value })} />
              </Field>
              <Field label="Success toast — description">
                <Input className={inputClass} value={c.contact?.successDescription || ""} onChange={(e) => setSection("contact", { ...(c.contact || {}), successDescription: e.target.value })} />
              </Field>
            </div>
          </>
        }
      />

      {/* Footer */}
      <Block title="Footer" hint="The small line at the bottom of every page.">
        <Field label="Tagline" hint='Shown after the © year and your name, e.g. "Built with care"'>
          <Input className={inputClass} value={c.footer?.tagline || ""} onChange={(e) => setSection("footer", { ...(c.footer || {}), tagline: e.target.value })} />
        </Field>
      </Block>
    </div>
  );
}
